import React from "react";
import { useNavigate } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import {
  FormControl,
  InputLabel,
  FormGroup,
  Select,
  MenuItem,
  Grid,
  TextField,
  Button,
  Container,
  Card,
  CardContent,
  Typography,
  Chip,
  OutlinedInput,
  Box,
  makeStyles,
} from "@material-ui/core";
import Autocomplete from "@mui/material/Autocomplete";
import LocationCityIcon from "@material-ui/icons/LocationCity";
import ArrowForwardIcon from "@material-ui/icons/ArrowForward";
import { Field, Form, Formik } from "formik";
import * as Yup from "yup";
import { connect } from "react-redux";
import { addHome } from "src/actions/HomeActions";
import PlacesAutocomplete from "react-places-autocomplete";
import { AMENITIES_LIST, COUNTRIES, RULES_LIST } from "src/utils/misc";

function getStyles(name, selected, theme) {
  return {
    fontWeight:
      selected.indexOf(name) === -1
        ? theme.typography.fontWeightRegular
        : theme.typography.fontWeightMedium,
  };
}

const TOKENS = {
  bg: "#F5F6FA",
  surface: "#FFFFFF",
  border: "#EEF0F5",
  borderStrong: "#E5E7EB",
  textPrimary: "#0F172A",
  textSecondary: "#64748B",
  textMuted: "#94A3B8",
  accent: "#222222",
  accentDark: "#000000",
};

const useStyles = makeStyles((theme) => ({
  root: {
    backgroundColor: TOKENS.bg,
    minHeight: "100%",
    paddingTop: theme.spacing(3),
    paddingBottom: theme.spacing(8),
  },
  pageHeader: {
    marginBottom: 24,
  },
  pageTitle: {
    fontSize: 24,
    fontWeight: 700,
    color: "#0F172A",
  },
  pageSubtitle: {
    fontSize: 14,
    color: "#64748B",
    marginTop: 4,
  },
  section: {
    borderRadius: 12,
    boxShadow: "0 1px 3px rgba(0,0,0,0.04), 0 1px 2px rgba(0,0,0,0.02)",
    border: "1px solid #EEF0F5",
    marginBottom: 20,
    overflow: "hidden",
  },
  sectionHeader: {
    padding: "20px 24px 0 24px",
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: 600,
    color: "#0F172A",
  },
  sectionSubtitle: {
    fontSize: 13,
    color: "#64748B",
    marginTop: 2,
  },
  sectionContent: {
    padding: "16px 24px 24px 24px",
  },
  fieldLabel: {
    fontSize: 13,
    fontWeight: 500,
    color: "#374151",
    marginBottom: 6,
    display: "block",
  },
  textField: {
    "& .MuiOutlinedInput-root": {
      borderRadius: 8,
      fontSize: 14,
      "& fieldset": {
        borderColor: "#E2E8F0",
      },
      "&:hover fieldset": {
        borderColor: "#A5B4FC",
      },
      "&.Mui-focused fieldset": {
        borderColor: "#222222",
      },
    },
    "& .MuiInputLabel-root": {
      fontSize: 14,
      color: "#64748B",
    },
  },
  selectField: {
    borderRadius: 8,
    fontSize: 14,
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: "#E2E8F0",
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: "#A5B4FC",
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: "#222222",
    },
  },
  chipSelected: {
    backgroundColor: "#222222 !important",
    color: "#FFFFFF !important",
    borderColor: "#222222 !important",
    fontWeight: 500,
    "& .MuiChip-deleteIcon": {
      color: "#FFFFFF",
    },
  },
  chipUnselected: {
    backgroundColor: "#FFFFFF",
    color: "#475569",
    borderColor: "#E2E8F0",
    fontWeight: 400,
    "&:hover": {
      backgroundColor: "#F8FAFC",
      borderColor: "#A5B4FC",
    },
  },
  chipGrid: {
    display: "flex",
    flexWrap: "wrap",
    gap: 8,
    marginTop: 8,
  },
  submitButton: {
    backgroundColor: "#222222",
    color: "#FFFFFF",
    borderRadius: 8,
    padding: "12px 32px",
    fontSize: 14,
    fontWeight: 600,
    textTransform: "none",
    boxShadow: "0 1px 2px rgba(34, 34, 34, 0.3)",
    "&:hover": {
      backgroundColor: "#000000",
      boxShadow: "0 4px 12px rgba(34, 34, 34, 0.4)",
    },
    "&:disabled": {
      backgroundColor: "#C7D2FE",
      color: "#FFFFFF",
    },
  },
  locationSuggestion: {
    display: "flex",
    alignItems: "center",
    padding: "10px 12px",
    cursor: "pointer",
    borderRadius: 8,
    marginTop: 4,
    transition: "background-color 0.15s ease",
  },
}));

function CreateHome({ addHome }) {
  const classes = useStyles();
  const navigate = useNavigate();
  const theme = useTheme();

  const [formState, setFormState] = React.useState({
    title: "",
    home_type: "Entire House",
    homeRules: "",
    description: "",
    price: "",
    homeAmenities: "",
    spaceToUse: "Entire House",
    numberOfGuestsExpected: "",
    bedroomCount: "",
    bedCount: "",
    bathroomCount: "",
    other_details: "",
    city: "",
    checkin_time: "",
    homeCountry: "",
    monthlyDiscount: 0,
    weeklyDiscount: 0,
    minStays: 1,
    maxStays: 356,
  });

  const [location, setLocation] = React.useState("");
  const [amenities, setAmenities] = React.useState([]);
  const [country, setCountry] = React.useState("Uganda");
  const [rules, setRules] = React.useState([]);

  const toggleAmenity = (name) => {
    setAmenities((prev) =>
      prev.includes(name) ? prev.filter((a) => a !== name) : [...prev, name],
    );
  };

  const toggleRule = (name) => {
    setRules((prev) =>
      prev.includes(name) ? prev.filter((r) => r !== name) : [...prev, name],
    );
  };

  return (
    <Container className={classes.root}>
      {/* Page Header */}
      <Box className={classes.pageHeader}>
        <Typography className={classes.pageTitle}>List a New Home</Typography>
        <Typography className={classes.pageSubtitle}>
          Properties like apartments, holiday homes, villas and more
        </Typography>
      </Box>

      <Formik
        initialValues={formState}
        validationSchema={Yup.object().shape({
          title: Yup.string()
            .required("Please enter the Home Title")
            .max(100, "Title can't exceed 100 characters"),
          description: Yup.string()
            .required("Please enter a description")
            .max(250, "Description can't exceed 250 characters"),
          price: Yup.number().required("Please enter the price (numbers only)"),
          numberOfGuestsExpected: Yup.number().required(
            "Please enter guest capacity",
          ),
          bedroomCount: Yup.string().required("Please enter bedroom count"),
          bedCount: Yup.string().required("Please enter bed count"),
          bathroomCount: Yup.string().required("Please enter bathroom count"),
          spaceToUse: Yup.string().required("Please enter the space to use"),
          other_details: Yup.string()
            .required("Please enter other details")
            .max(200, "Other details can't exceed 200 characters"),
          city: Yup.string().required("Please enter the home address"),
          checkin_time: Yup.string().required("Please enter check-in time"),
          minStays: Yup.number(),
          maxStays: Yup.number(),
          monthlyDiscount: Yup.number(),
          weeklyDiscount: Yup.number(),
        })}
        onSubmit={async (values) => {
          const amenities_data = amenities.map((amenity) => ({
            name: amenity,
          }));
          const rules_data = rules.map((rule) => ({ name: rule }));
          const obj = {
            ...values,
            spaceToUse: formState.spaceToUse,
            home_type: formState.home_type,
            address: location,
            availableDates: [],
            amenities: amenities_data,
            country,
            rules: rules_data,
          };
          await addHome(obj, navigate);
        }}
      >
        {({
          errors,
          handleBlur,
          handleChange,
          handleSubmit,
          isSubmitting,
          touched,
          values,
        }) => (
          <Form noValidate onSubmit={handleSubmit}>
            {/* Basic Information */}
            <Card className={classes.section}>
              <Box className={classes.sectionHeader}>
                <Typography className={classes.sectionTitle}>
                  Basic Information
                </Typography>
                <Typography className={classes.sectionSubtitle}>
                  Property type, title, and location
                </Typography>
              </Box>
              <CardContent className={classes.sectionContent}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Field
                      as={TextField}
                      className={classes.textField}
                      error={Boolean(touched.title && errors.title)}
                      helperText={touched.title && errors.title}
                      variant="outlined"
                      required
                      fullWidth
                      id="title"
                      label="Listing Title"
                      name="title"
                      type="text"
                      onBlur={handleBlur}
                      onChange={handleChange}
                      placeholder="Catch guests' attention with a title that highlights what makes your place special"
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <FormControl
                      variant="outlined"
                      fullWidth
                      className={classes.textField}
                    >
                      <InputLabel>Property Type</InputLabel>
                      <Select
                        className={classes.selectField}
                        onChange={(e) =>
                          setFormState({
                            ...formState,
                            home_type: e.target.value,
                          })
                        }
                        label="Property Type"
                        value={formState.home_type}
                      >
                        {[
                          "Secondary Unit",
                          "Entire House",
                          "Bed and Breakfast",
                          "Boutique",
                          "Apartment",
                          "Hotel Room",
                        ].map((item) => (
                          <MenuItem key={item} value={item}>
                            {item}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <FormControl
                      variant="outlined"
                      fullWidth
                      className={classes.textField}
                    >
                      <InputLabel>What will guests have?</InputLabel>
                      <Select
                        className={classes.selectField}
                        onChange={(e) =>
                          setFormState({
                            ...formState,
                            spaceToUse: e.target.value,
                          })
                        }
                        label="What will guests have?"
                        value={formState.spaceToUse}
                      >
                        {[
                          "Entire House",
                          "Private Room",
                          "Shared Room",
                          "All Apartment",
                        ].map((item) => (
                          <MenuItem key={item} value={item}>
                            {item}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <Autocomplete
                      disableClearable
                      fullWidth
                      options={COUNTRIES}
                      onChange={(event, newValue) => setCountry(newValue)}
                      inputValue={country}
                      onInputChange={(event, newInputValue) =>
                        setCountry(newInputValue)
                      }
                      autoHighlight
                      getOptionLabel={(option) => option}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          className={classes.textField}
                          variant="outlined"
                          label="Country"
                          inputProps={{
                            ...params.inputProps,
                            autoComplete: "new-password",
                          }}
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <Field
                      as={TextField}
                      className={classes.textField}
                      error={Boolean(touched.city && errors.city)}
                      helperText={touched.city && errors.city}
                      variant="outlined"
                      required
                      fullWidth
                      id="city"
                      label="District / City"
                      name="city"
                      type="text"
                      onBlur={handleBlur}
                      onChange={handleChange}
                      placeholder="e.g. Kampala, Naalya Estate"
                    />
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <PlacesAutocomplete
                      value={location}
                      onChange={setLocation}
                      onSelect={setLocation}
                    >
                      {({
                        getInputProps,
                        suggestions,
                        getSuggestionItemProps,
                        loading,
                      }) => (
                        <div>
                          <TextField
                            {...getInputProps({
                              variant: "outlined",
                              label: "Google Maps Location",
                              placeholder: "Search for location",
                              fullWidth: true,
                              className: classes.textField,
                            })}
                          />
                          {(loading || suggestions.length > 0) && (
                            <Box
                              style={{
                                border: "1px solid #E2E8F0",
                                borderRadius: 8,
                                marginTop: 4,
                                overflow: "hidden",
                              }}
                            >
                              {loading && (
                                <Box style={{ padding: 12 }}>
                                  <Typography
                                    variant="caption"
                                    color="textSecondary"
                                  >
                                    Searching...
                                  </Typography>
                                </Box>
                              )}
                              {suggestions.map((suggestion) => (
                                <div
                                  {...getSuggestionItemProps(suggestion, {
                                    className: classes.locationSuggestion,
                                    style: {
                                      backgroundColor: suggestion.active
                                        ? "#F1F5F9"
                                        : "#FFFFFF",
                                    },
                                  })}
                                >
                                  <LocationCityIcon
                                    style={{
                                      marginRight: 10,
                                      color: "#94A3B8",
                                      fontSize: 18,
                                    }}
                                  />
                                  <Typography
                                    variant="body2"
                                    style={{ fontSize: 13 }}
                                  >
                                    {suggestion.description}
                                  </Typography>
                                </div>
                              ))}
                            </Box>
                          )}
                        </div>
                      )}
                    </PlacesAutocomplete>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {/* Capacity */}
            <Card className={classes.section}>
              <Box className={classes.sectionHeader}>
                <Typography className={classes.sectionTitle}>
                  Capacity
                </Typography>
                <Typography className={classes.sectionSubtitle}>
                  How many guests can your space accommodate?
                </Typography>
              </Box>
              <CardContent className={classes.sectionContent}>
                <Grid container spacing={2}>
                  <Grid item xs={6} md={3}>
                    <Field
                      as={TextField}
                      className={classes.textField}
                      error={Boolean(
                        touched.numberOfGuestsExpected &&
                        errors.numberOfGuestsExpected,
                      )}
                      helperText={
                        touched.numberOfGuestsExpected &&
                        errors.numberOfGuestsExpected
                      }
                      variant="outlined"
                      required
                      fullWidth
                      InputProps={{ inputProps: { min: 0 } }}
                      label="Max Guests"
                      name="numberOfGuestsExpected"
                      type="number"
                      onBlur={handleBlur}
                      onChange={handleChange}
                    />
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Field
                      as={TextField}
                      className={classes.textField}
                      error={Boolean(
                        touched.bedroomCount && errors.bedroomCount,
                      )}
                      helperText={touched.bedroomCount && errors.bedroomCount}
                      variant="outlined"
                      required
                      fullWidth
                      InputProps={{ inputProps: { min: 0 } }}
                      label="Bedrooms"
                      name="bedroomCount"
                      type="number"
                      onBlur={handleBlur}
                      onChange={handleChange}
                    />
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Field
                      as={TextField}
                      className={classes.textField}
                      error={Boolean(touched.bedCount && errors.bedCount)}
                      helperText={touched.bedCount && errors.bedCount}
                      variant="outlined"
                      required
                      fullWidth
                      InputProps={{ inputProps: { min: 0 } }}
                      label="Beds"
                      name="bedCount"
                      type="number"
                      onBlur={handleBlur}
                      onChange={handleChange}
                    />
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Field
                      as={TextField}
                      className={classes.textField}
                      error={Boolean(
                        touched.bathroomCount && errors.bathroomCount,
                      )}
                      helperText={touched.bathroomCount && errors.bathroomCount}
                      variant="outlined"
                      required
                      fullWidth
                      InputProps={{ inputProps: { min: 0 } }}
                      label="Bathrooms"
                      name="bathroomCount"
                      type="number"
                      onBlur={handleBlur}
                      onChange={handleChange}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {/* Description */}
            <Card className={classes.section}>
              <Box className={classes.sectionHeader}>
                <Typography className={classes.sectionTitle}>
                  Description
                </Typography>
                <Typography className={classes.sectionSubtitle}>
                  Tell guests about the space
                </Typography>
              </Box>
              <CardContent className={classes.sectionContent}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Field
                      as={TextField}
                      className={classes.textField}
                      error={Boolean(touched.description && errors.description)}
                      helperText={touched.description && errors.description}
                      variant="outlined"
                      required
                      fullWidth
                      multiline
                      minRows={3}
                      label="Full Description"
                      name="description"
                      onBlur={handleBlur}
                      onChange={handleChange}
                      placeholder="Write a quick summary of your place. Highlight what's special about your space, the neighborhood, and how you'll interact with guests."
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Field
                      as={TextField}
                      className={classes.textField}
                      variant="outlined"
                      required
                      fullWidth
                      label="Spaces guests can use"
                      name="spaceToUse"
                      onBlur={handleBlur}
                      onChange={handleChange}
                      placeholder="e.g. Kitchen, laundry, pool"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Field
                      as={TextField}
                      className={classes.textField}
                      error={Boolean(
                        touched.other_details && errors.other_details,
                      )}
                      helperText={touched.other_details && errors.other_details}
                      variant="outlined"
                      required
                      fullWidth
                      label="Other details"
                      name="other_details"
                      onBlur={handleBlur}
                      onChange={handleChange}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {/* Amenities */}
            <Card className={classes.section}>
              <Box className={classes.sectionHeader}>
                <Typography className={classes.sectionTitle}>
                  Amenities
                </Typography>
                <Typography className={classes.sectionSubtitle}>
                  Select what your home offers —{" "}
                  {amenities.length > 0
                    ? `${amenities.length} selected`
                    : "none selected"}
                </Typography>
              </Box>
              <CardContent className={classes.sectionContent}>
                <Box className={classes.chipGrid}>
                  {AMENITIES_LIST.map((name) => (
                    <Chip
                      key={name}
                      label={amenities.includes(name) ? `✓ ${name}` : name}
                      clickable
                      variant="outlined"
                      size="small"
                      className={
                        amenities.includes(name)
                          ? classes.chipSelected
                          : classes.chipUnselected
                      }
                      onClick={() => toggleAmenity(name)}
                    />
                  ))}
                </Box>
              </CardContent>
            </Card>

            {/* House Rules */}
            <Card className={classes.section}>
              <Box className={classes.sectionHeader}>
                <Typography className={classes.sectionTitle}>
                  House Rules
                </Typography>
                <Typography className={classes.sectionSubtitle}>
                  Set expectations for guests —{" "}
                  {rules.length > 0
                    ? `${rules.length} selected`
                    : "none selected"}
                </Typography>
              </Box>
              <CardContent className={classes.sectionContent}>
                <Box className={classes.chipGrid}>
                  {RULES_LIST.map((name) => (
                    <Chip
                      key={name}
                      label={rules.includes(name) ? `✓ ${name}` : name}
                      clickable
                      variant="outlined"
                      size="small"
                      className={
                        rules.includes(name)
                          ? classes.chipSelected
                          : classes.chipUnselected
                      }
                      onClick={() => toggleRule(name)}
                    />
                  ))}
                </Box>
              </CardContent>
            </Card>

            {/* Pricing */}
            <Card className={classes.section}>
              <Box className={classes.sectionHeader}>
                <Typography className={classes.sectionTitle}>
                  Pricing
                </Typography>
                <Typography className={classes.sectionSubtitle}>
                  Nightly rate and check-in details
                </Typography>
              </Box>
              <CardContent className={classes.sectionContent}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <Field
                      as={TextField}
                      className={classes.textField}
                      error={Boolean(touched.price && errors.price)}
                      helperText={touched.price && errors.price}
                      variant="outlined"
                      required
                      fullWidth
                      InputProps={{ inputProps: { min: 1 } }}
                      label="Price per night (USD)"
                      name="price"
                      onBlur={handleBlur}
                      onChange={handleChange}
                      value={values.price}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Field
                      as={TextField}
                      className={classes.textField}
                      error={Boolean(
                        touched.checkin_time && errors.checkin_time,
                      )}
                      helperText={touched.checkin_time && errors.checkin_time}
                      variant="outlined"
                      required
                      fullWidth
                      label="Check-in Time"
                      name="checkin_time"
                      type="text"
                      onBlur={handleBlur}
                      onChange={handleChange}
                      placeholder="e.g. Flexible, 2:00 PM"
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {/* Stay Duration */}
            <Card className={classes.section}>
              <Box className={classes.sectionHeader}>
                <Typography className={classes.sectionTitle}>
                  Stay Duration
                </Typography>
                <Typography className={classes.sectionSubtitle}>
                  How long can guests stay? (Optional)
                </Typography>
              </Box>
              <CardContent className={classes.sectionContent}>
                <Grid container spacing={2}>
                  <Grid item xs={6} md={3}>
                    <Field
                      as={TextField}
                      className={classes.textField}
                      variant="outlined"
                      fullWidth
                      InputProps={{ inputProps: { min: 1 } }}
                      label="Min Nights"
                      name="minStays"
                      type="number"
                      onBlur={handleBlur}
                      onChange={handleChange}
                      value={values.minStays}
                    />
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Field
                      as={TextField}
                      className={classes.textField}
                      variant="outlined"
                      fullWidth
                      InputProps={{ inputProps: { min: 1, max: 365 } }}
                      label="Max Nights"
                      name="maxStays"
                      type="number"
                      onBlur={handleBlur}
                      onChange={handleChange}
                      value={values.maxStays}
                    />
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Field
                      as={TextField}
                      className={classes.textField}
                      variant="outlined"
                      fullWidth
                      InputProps={{ inputProps: { min: 0 } }}
                      label="Weekly Discount (%)"
                      name="weeklyDiscount"
                      type="number"
                      onBlur={handleBlur}
                      onChange={handleChange}
                      value={values.weeklyDiscount}
                    />
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Field
                      as={TextField}
                      className={classes.textField}
                      variant="outlined"
                      fullWidth
                      InputProps={{ inputProps: { min: 0 } }}
                      label="Monthly Discount (%)"
                      name="monthlyDiscount"
                      type="number"
                      onBlur={handleBlur}
                      onChange={handleChange}
                      value={values.monthlyDiscount}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {/* Submit */}
            <Box
              display="flex"
              justifyContent="flex-end"
              style={{ marginTop: 8 }}
            >
              <Button
                type="submit"
                disabled={isSubmitting}
                variant="contained"
                className={classes.submitButton}
                endIcon={<ArrowForwardIcon />}
              >
                Continue to Add Images
              </Button>
            </Box>
          </Form>
        )}
      </Formik>
    </Container>
  );
}

export default connect(null, { addHome })(CreateHome);
